import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { Search } from "lucide-react";
import { useEffect, useRef } from "react";

/**
 * The search half of a page's action row: a field, or the bare magnifier it
 * folds into.
 *
 * Leads the row, so the field grows rightward into the space the filters give
 * up. Whether it stands open is the row's call, not this component's — see
 * {@link expanded} — because a phone seats only one of the two at full size.
 * Folded, it shows the same glyph the open field leads with, so the two states
 * read as one control.
 *
 * `SettingsPanelHeader` does the same thing for the settings panels, but its
 * copy is wired into that chrome's portals and takeover context; this composes
 * the same `Input` and `Button` without them.
 */
export type PanelToolsSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  statsigName: string;
  /**
   * Whether this is an open, focused field or the glyph that asks for one.
   *
   * The whole of the answer, every render: this component keeps no open state of
   * its own, and {@link onOpenChange} is a request rather than a change that has
   * already happened. A host that ignores the request leaves the field as it was,
   * which is what lets a wide row keep it standing through every blur.
   */
  expanded?: boolean;
  /** The closed glyph's box, sized to match the controls beside it. */
  controlClassName: string;
  /**
   * Whether the open field should take the row rather than sit at a measured
   * width. The row decides, from its own width — see {@link PanelToolbar} — and
   * not from the viewport: these pages live in a column the user can drag
   * narrow while the window stays wide.
   */
  fillRow: boolean;
  /**
   * Whether to take the caret on arrival, for a page whose whole job is the
   * search — not for a list someone came to browse, where holding the caret
   * turns every keystroke into a query and takes Space away from scrolling.
   *
   * Opening the field by hand always focuses it, with or without this.
   */
  focusOnMount: boolean;
  /**
   * Asks the host to open or dismiss the field — a tap on the glyph, a blur, an
   * Escape.
   *
   * A request, not a report: the host decides, because the controls that trade
   * places with the field are its to render, and on a wide row it simply
   * declines and the field stays open.
   */
  onOpenChange?: (open: boolean) => void;
};

export const PanelToolsSearch = ({
  value,
  onChange,
  placeholder,
  statsigName,
  expanded = false,
  controlClassName,
  fillRow,
  focusOnMount,
  onOpenChange,
}: PanelToolsSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Open entirely on the host's say-so, with no copy of its own. It used to keep
  // one and take the union of the two, which meant a field the user had opened
  // by tapping stayed open after the host had given the row to something else —
  // the two disagreed and the louder one won. Asking to open and being open are
  // different things, and only the host can know the second.
  const open = expanded;

  // Focus on the way open, which is the whole point of the gesture: reaching for
  // the glyph should leave the user typing, not hunting for the field that just
  // appeared. Tracked against the previous value rather than keyed on `open`
  // alone, because a wide row is born open — so keying on `open` focused on
  // arrival on every page that has a field, and on every remount, which meant
  // backing out of a drilled-in screen dropped the caret into the search box
  // instead of returning it to the list.
  // Seeded as already-open when the page is not claiming the caret, so a row that
  // is born open does not read as having just opened.
  const wasOpen = useRef(open && !focusOnMount);
  useEffect(() => {
    if (open && !wasOpen.current) inputRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // Escape clears the query. It also puts the field away — except where the
  // field is the view, since collapsing there backs out of nothing and leaves
  // the page without the control it exists for.
  const close = () => {
    onChange("");
    onOpenChange?.(false);
  };

  return (
    // The swap is a crossfade, not a slide: animating the width reads as motion
    // across the whole header for a control that is simply now open, and drags
    // the icons beside it on the way.
    <div
      className={cn(
        "relative flex items-center",
        // Open on a narrow row the field takes it, since a user who has just
        // reached for search is searching and the filters can wait behind it.
        // A wide row gives it a measured width instead and leaves the rest to
        // the segments beside it. `flex-1` rather than `w-full`, which would
        // overflow the row and shove the controls past the gutter.
        //
        // Closed, the wrapper hugs the control inside it, whatever size its host
        // draws that at — a fixed width here would have to be kept in step with
        // `controlClassName`, and the two drifting apart is what puts the icons
        // inside the gutter.
        open && (fillRow ? "min-w-0 flex-1" : "w-66 flex-none"),
        !open && "w-auto shrink-0",
      )}
    >
      {open ? (
        <>
          {/* The same glyph, now inside the field and leading its placeholder —
              the field replaces the button rather than sitting next to it, so
              there is never a second magnifier on the bar. */}
          <Search className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground animate-in fade-in duration-150 motion-reduce:animate-none" />
          <Input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                // Kept off the page's Escape handling: this closes the field,
                // it does not navigate.
                event.stopPropagation();
                close();
              }
            }}
            // Touching anything outside puts the field away and gives the row
            // back to the controls it was covering — on a phone that row is the
            // only one there is, so leaving the field standing would strand the
            // filters behind it. A query does not hold it open, but it does
            // light the glyph it collapses to, so a narrowed list still says
            // what narrowed it.
            //
            // A field the host renders `expanded` never collapses: the row has
            // the width for it, and reaching for the filter beside it blurs the
            // input, which would otherwise take the field away mid-gesture.
            onBlur={() => onOpenChange?.(false)}
            placeholder={placeholder}
            aria-label={placeholder}
            data-statsig-name={statsigName}
            // `h-7` to sit level with the segments and glyphs beside it, which all
            // take the row's 28px. `md:text-xs` beats `Input`'s own `md:text-sm`:
            // tailwind-merge keys on the modifier, so a bare `text-xs` leaves the
            // media-query rule standing and the field alone steps up at 768px.
            className="h-7 w-full pl-8 text-xs animate-in fade-in duration-150 motion-reduce:animate-none md:text-xs"
          />
        </>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn("shrink-0", controlClassName)}
              // Says what the glyph now stands for. A bare "Search" over a list
              // already cut down by a query the field is no longer showing
              // describes a page the user is not on.
              aria-label={value ? `${placeholder}: ${value}` : placeholder}
              aria-expanded={false}
              data-statsig-name={`${statsigName}-toggle`}
              onClick={() => onOpenChange?.(true)}
            >
              {/* Lit while a query is still narrowing the list, the same tint
                  the filter trigger wears when an axis is applied — otherwise
                  collapsing the field takes away the only sign of why the list
                  below is short. */}
              <Search className={cn(value && "text-primary")} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{value ? `${placeholder}: ${value}` : placeholder}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
